<template>
  <div class="inline-flex items-center">
    <span
      :class="badgeClasses"
      class="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium"
    >
      <div :class="dotClasses" class="mr-1 h-2 w-2 rounded-full"></div>
      {{ statusText }}
    </span>
    <div v-if="showTooltip" class="ml-2">
      <div class="group relative">
        <svg
          class="h-4 w-4 text-muted-foreground cursor-help"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <path d="M12 17h.01" />
        </svg>
        <div
          class="absolute bottom-full left-1/2 mb-2 hidden -translate-x-1/2 transform rounded-md bg-popover px-3 py-2 text-sm text-popover-foreground shadow-lg group-hover:block"
        >
          {{ tooltipText }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { formatTimeAgo } from '@/lib/utils'

interface Props {
  isCached: boolean
  timestamp: string | null
  ageSeconds: number | null
  showTooltip?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showTooltip: true
})

const statusText = computed(() => {
  if (!props.isCached || !props.timestamp) {
    return 'Live'
  }
  
  if (props.ageSeconds !== null) {
    return formatTimeAgo(props.timestamp)
  }
  
  return 'Cached'
})

const tooltipText = computed(() => {
  if (!props.isCached || !props.timestamp) {
    return 'Fresh data from API'
  }
  
  return `Cached data from ${new Date(props.timestamp).toLocaleString()}`
})

const badgeClasses = computed(() => {
  if (!props.isCached) {
    return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
  }
  
  return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
})

const dotClasses = computed(() => {
  if (!props.isCached) {
    return 'bg-green-500'
  }
  
  return 'bg-yellow-500'
})
</script>
